// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { EducBox, InputRow, fmt } from './shared';

const fmtM = (n) => isFinite(n) ? Math.round(n) : null;

const CartaRevolvingCalc = () => {
  const [saldo, setSaldo] = useState('2000');
  const [taeg, setTaeg] = useState('21');
  const [rata, setRata] = useState('50');
  const [openItem, setOpenItem] = useState(null);

  const S = parseFloat(saldo) || 0;
  const r = parseFloat(taeg) / 100 / 12;
  const R = parseFloat(rata) || 0;
  const interestMensile = S * r;

  const calcMesi = (saldo0, rataM) => {
    if (rataM <= saldo0 * r) return null;
    let s = saldo0, m = 0;
    while (s > 0 && m < 600) { s = s * (1 + r) - rataM; m++; }
    return m;
  };
  const calcInteressi = (saldo0, rataM) => {
    const m = calcMesi(saldo0, rataM);
    if (!m) return null;
    return rataM * m - saldo0;
  };
  const rataPer12 = r > 0 ? S * (r * Math.pow(1 + r, 12)) / (Math.pow(1 + r, 12) - 1) : S / 12;
  const rataMin2 = Math.max(Math.ceil(S * 0.02), 25);

  const mesiInput = calcMesi(S, R);
  const interessiInput = calcInteressi(S, R);
  const mesiMin = calcMesi(S, rataMin2);
  const interessiMin = calcInteressi(S, rataMin2);
  const interessiPer12 = calcInteressi(S, rataPer12);

  const fatalDebt = R <= interestMensile;

  const accordion = [
    { title: 'Carta a saldo vs carta rateale: la differenza fondamentale', body: 'La carta A SALDO ti permette di spendere durante il mese e pagare tutto in un\'unica soluzione entro la data di scadenza — di solito il 15 o il 31 del mese successivo. Se paghi l\'INTERO importo entro quella data non paghi nessun interesse. Zero. È lo strumento più conveniente se usato correttamente.\n\nLa carta RATEALE (revolving) ti permette invece di pagare solo una quota ogni mese — solitamente il 2-5% del saldo. Il resto rimane come debito e accumula interessi al 18-24% annuo DAL GIORNO STESSO della spesa, senza alcun periodo di grazia.\n\nAttenzione: molte carte offrono ENTRAMBE le modalità. Le banche spingono verso il rateale perché è molto più redditizio per loro. Leggi sempre il contratto e imposta il pagamento a saldo come default.' },
    { title: 'La trappola del pagamento parziale', body: 'Cosa succede se paghi parzialmente? Esempio: hai €1.000 di saldo e paghi €800. Potresti pensare di pagare interessi solo sui €200 rimanenti. SBAGLIATO. In molti contratti revolving gli interessi scattano sull\'INTERO importo originale di €1.000 dal giorno della spesa — non solo sul residuo. Questa clausola è spesso nel contratto ma raramente spiegata chiaramente. Leggi sempre le condizioni generali della tua carta.' },
    { title: 'Come leggere l\'estratto conto', body: 'L\'estratto conto mensile mostra:\n- Data estratto conto: quando viene calcolato il saldo\n- Data scadenza pagamento: entro quando pagare per non pagare interessi\n- Saldo totale dovuto: paga QUESTO importo per non pagare interessi\n- Pagamento minimo: paga SOLO questo se vuoi pagare interessi al 20%+\n- TAEG: il tasso reale annuo applicato\nSe vedi la voce "interessi addebitati" nell\'estratto stai già pagando la trappola.' },
    { title: 'Il prelievo ATM con carta di credito', body: 'Prelevare contanti con la carta di credito è la cosa più costosa che puoi fare con essa. Gli interessi scattano immediatamente — non aspettano la fine del mese come per gli acquisti.\nCommissione tipica: 3-5% del prelievo (minimo €2-5) + interessi dal primo giorno.\nCon TAEG 21% su un prelievo di €200: paghi €6-10 di commissione subito + interessi dal giorno stesso. Usa SEMPRE la carta di debito per i prelievi ATM.' },
    { title: 'I vantaggi reali se usata bene', body: 'Usata correttamente — pagando sempre il saldo completo entro scadenza — la carta di credito offre vantaggi reali:\n- Protezione acquisti: se il venditore non consegna puoi fare chargeback\n- Cashback: 0.5-2% sugli acquisti con alcune carte\n- Punti e miles: per chi viaggia spesso\n- Assicurazione viaggi e acquisti: inclusa in molte carte premium\n- Zero interessi: se paghi tutto entro scadenza ogni mese\nLe migliori carte cashback italiane 2026: American Express Blu (1% cashback), Widiba Mastercard, ING Direct.' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div style={{ background: 'rgba(201,168,76,0.06)', borderLeft: '3px solid #C9A84C', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#888780', lineHeight: 1.6 }}>
        <strong style={{ color: '#C9A84C' }}>La carta revolving è la trappola finanziaria più comune in Italia.</strong> Con tassi del 18-24% annuo, pagare solo la rata minima trasforma un debito piccolo in un problema enorme negli anni.
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InputRow label="Saldo attuale sulla carta (€)" value={saldo} onChange={setSaldo} prefix="€" />
        <InputRow label="TAEG applicato (%)" value={taeg} onChange={setTaeg} suffix="%" tooltip="Trovi il TAEG nell'estratto conto o nel contratto. La media italiana è 18-24% annuo." />
        <InputRow label="Rata mensile che paghi (€)" value={rata} onChange={setRata} prefix="€" tooltip="La rata minima è solitamente il 2-5% del saldo. Sembra conveniente ma è una trappola." />
      </div>

      {fatalDebt && (
        <div className="mb-4 p-4 rounded-xl text-sm font-medium" style={{ background: 'rgba(226,75,74,0.1)', border: '1px solid rgba(226,75,74,0.4)', color: '#F08080' }}>
          ⚠ La tua rata non copre nemmeno gli interessi mensili (€{fmt(interestMensile)}/mese) — il debito sta crescendo.
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl" style={{ background: 'rgba(226,75,74,0.08)', border: '1px solid rgba(226,75,74,0.3)' }}>
          <p className="text-xs mb-1" style={{ color: '#F08080' }}>Mesi per estinguere</p>
          <p className="text-2xl font-bold text-[#E8E8E4]">{fatalDebt ? '∞' : mesiInput ? `${mesiInput} mesi` : '—'}</p>
          {mesiInput && <p className="text-xs mt-1" style={{ color: '#888780' }}>{(mesiInput / 12).toFixed(1)} anni</p>}
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'rgba(226,75,74,0.08)', border: '1px solid rgba(226,75,74,0.3)' }}>
          <p className="text-xs mb-1" style={{ color: '#F08080' }}>Totale interessi pagati</p>
          <p className="text-2xl font-bold text-[#E8E8E4]">{fatalDebt ? '∞' : interessiInput != null ? `€${fmt(interessiInput)}` : '—'}</p>
          {interessiInput && <p className="text-xs mt-1" style={{ color: '#888780' }}>{fmt(interessiInput / S * 100)}% del debito iniziale</p>}
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
          <p className="text-xs mb-1" style={{ color: '#C9A84C' }}>Rata per chiudere in 12 mesi</p>
          <p className="text-2xl font-bold text-[#E8E8E4]">€{fmt(rataPer12)}/mese</p>
          <p className="text-xs mt-1" style={{ color: '#888780' }}>Interessi totali: €{fmtM(interessiPer12) != null ? fmt(interessiPer12) : '—'}</p>
        </div>
      </div>

      {/* Comparison table */}
      <div className="mb-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="px-4 py-3" style={{ background: '#252523', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-sm font-medium text-[#E8E8E4]">Confronto scenari</p>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <td className="px-4 py-3 text-[#888780]">Rata minima banca (2% del saldo): €{fmt(rataMin2)}/mese</td>
              <td className="px-4 py-3 text-[#E8E8E4] text-right">{mesiMin ? `${mesiMin} mesi` : '∞'}</td>
              <td className="px-4 py-3 text-[#E8E8E4] text-right">{interessiMin != null ? `€${fmt(interessiMin)} interessi` : '∞'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <td className="px-4 py-3 text-[#888780]">La tua rata attuale: €{fmt(R)}/mese</td>
              <td className="px-4 py-3 text-[#E8E8E4] text-right">{fatalDebt ? '∞' : mesiInput ? `${mesiInput} mesi` : '—'}</td>
              <td className="px-4 py-3 text-[#E8E8E4] text-right">{fatalDebt ? '∞' : interessiInput != null ? `€${fmt(interessiInput)} interessi` : '—'}</td>
            </tr>
            <tr style={{ background: 'rgba(61,122,82,0.06)' }}>
              <td className="px-4 py-3 font-medium" style={{ color: '#9FE1CB' }}>Per chiudere in 12 mesi: €{fmt(rataPer12)}/mese ✓</td>
              <td className="px-4 py-3 font-medium text-right" style={{ color: '#9FE1CB' }}>12 mesi</td>
              <td className="px-4 py-3 font-medium text-right" style={{ color: '#9FE1CB' }}>{fmtM(interessiPer12) != null ? `€${fmt(interessiPer12)} interessi` : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Warning box */}
      <div className="mb-6 p-4 rounded-xl text-sm leading-relaxed" style={{ background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.3)', color: '#F08080' }}>
        <strong>⚠ Esempio reale:</strong> €2.000 di debito al 21% TAEG pagando €50/mese → oltre 5 anni per estinguere il debito e oltre €850 di soli interessi pagati. La soluzione: paga sempre più della rata minima, o meglio, estingui il saldo completo ogni mese entro la data di scadenza.
      </div>

      {/* Educational accordion */}
      <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden' }}>
        <div className="px-4 py-3" style={{ background: '#252523', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-sm font-medium text-[#E8E8E4]">Carte di credito: tutto quello che devi sapere</p>
        </div>
        {accordion.map((item, i) => (
          <div key={i} style={{ borderBottom: i < accordion.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
            <button
              onClick={() => setOpenItem(openItem === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
            >
              <span className="text-sm text-[#E8E8E4]">{item.title}</span>
              <ChevronDown className={`w-4 h-4 text-[#C9A84C] shrink-0 ml-3 transition-transform ${openItem === i ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {openItem === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
                  <div className="px-4 pb-4 text-sm text-[#888780] leading-relaxed whitespace-pre-line">{item.body}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
</motion.div>
  );
};

export default CartaRevolvingCalc;
